import type { AppFooterApp } from "./types";

/**
 * Conteúdo padrão do footer. Só é aplicado quando a aplicação não passa a prop
 * correspondente — qualquer campo pode ser sobrescrito.
 *
 * Dois níveis de default, por origem do texto:
 *  - `CUB_TENANT_CONTENT` — dados da CUB como empresa (razão social, CNPJ,
 *    contatos, endereço). Só vale para `tenant="cub"`.
 *  - `APP_CONTENT` — texto que descreve o comportamento da aplicação (aviso de
 *    CIP e encargos do boleto, frases de privacidade e do Validador). Vale para
 *    os dois tenants, porque descreve o meio de pagamento, não a marca.
 */

/** Dados da CUB — `tenant="cub"` apenas. */
export const CUB_TENANT_CONTENT = {
  companyName: "CUB Servicos de Tecnologia LTDA",
  companyDocument: "45.405.205/0001-95",
  supportEmail: "suporte@cub.com.br",
  whatsapp: "(11) 4200-6020",
  phone: "(11) 4200-6020",
  address: "R. Dante Carraro, 94 - Pinheiros, São Paulo - SP, 01443-010, Brasil",
  availability: "De segunda à sexta, das 9h às 18h, exceto feriados.",
  /**
   * Redes sociais da CUB — fixas. O footer ignora `socialLinks` no tenant
   * `"cub"` justamente para não haver duas fontes de verdade.
   */
  socialLinks: {
    instagram: "https://www.instagram.com/somoscub/",
    linkedin: "https://www.linkedin.com/company/somoscub",
    youtube: "https://www.youtube.com/@somos_CUB",
  },
  /** Texto institucional — só `app="statement-viewer"`. */
  companyText:
    "A CUB atua no mercado de crédito imobiliário, ajudando compradores de imóveis em sua jornada de financiamento, a concretizar investimentos. Através de tecnologia e design, trazemos simplicidade, transparência e facilidade para nossos clientes.",
} as const;

/** Rótulos fixos da interface. */
export const LABELS = {
  allRightsReserved: "Todos os direitos reservados.",
  support: "Atendimento:",
  address: "Endereço:",
  availability: "Disponibilidade:",
  partnersAndInvestors: "Parceiros e investidores",
  privacyPolicy: "Termo de privacidade",
  billingValidatorLabel: "cub.com.br",
  billingValidator:
    "Garanta sua segurança nos pagamentos, valide boletos e Pix no Validador de Cobranças: ",
} as const;

/** Frases de privacidade — o verbo muda com a aplicação. */
export const PRIVACY_SENTENCE: Record<AppFooterApp, { before: string; after: string }> = {
  "statement-viewer": {
    before: "Para mais informações sobre segurança, acesse o ",
    after: " da CUB.",
  },
  "billing-viewer": {
    before: "Ao realizar este pagamento você declara que leu e concordou com o ",
    after: " da plataforma CUB.",
  },
};

/**
 * Links institucionais da plataforma CUB. Não são do tenant: o footer white
 * label também aponta para o "Termo de privacidade da plataforma CUB".
 */
export const PLATFORM_LINKS = {
  privacyPolicyHref: "https://cub.com.br/assets/termo_privacidade.pdf",
  billingValidatorHref: "https://cub.com.br",
} as const;

/** Aviso de registro na CIP — `app="billing-viewer"`, nos dois tenants. */
export const CIP_NOTICE =
  "Este pagamento é registrado na CIP (Câmara Interbancária de Pagamentos), por isso você pode realizar o pagamento através do internet banking ou app da sua instituição de pagamento preferida.";

/** Casas decimais de cada encargo, conforme a spec do Figma (1,00% · 0,0033%). */
const LATE_FEE_DIGITS = 2;
const DAILY_INTEREST_DIGITS = 4;

const formatPercent = (value: number, digits: number) =>
  `${new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value)}%`;

/**
 * Frase de encargos do boleto. Retorna `undefined` quando multa ou mora não
 * foram informadas — encargo é dado do contrato, o DS não inventa valor.
 */
export function lateChargesSentence(
  lateFeePercent?: number,
  dailyInterestPercent?: number,
): string | undefined {
  if (lateFeePercent == null || dailyInterestPercent == null) return undefined;
  return (
    `Após o vencimento, é cobrada uma multa de ${formatPercent(lateFeePercent, LATE_FEE_DIGITS)}` +
    ` e mora de ${formatPercent(dailyInterestPercent, DAILY_INTEREST_DIGITS)} ao dia.` +
    " Se a data de vencimento coincidir com finais de semana ou feriados será considerado o próximo dia útil."
  );
}
